import React, { useState, useEffect, useCallback } from 'react';
import { ModernButton } from '../ui/ModernButton';
import { FlowingLines } from '../ui/FlowingLines';
import { ArrowLeftIcon, TrashIcon, SaveIcon, MinusCircleIcon, EraserIcon } from '../ui/Icons';
import quizTemplateService from '../../services/QuizTemplateService';
import imageUploadService from '../../services/ImageUploadService';
import { createQuestionPage, normalizeQuestionnaireData } from '../../utils/questionnairePages';

const ensureSingleQuizPageData = (data = {}) => {
    const normalized = normalizeQuestionnaireData(data);
    const flattenedQuestions = (normalized.questions || []).map((question) => ({ ...question }));
    const firstPage = normalized.questionPages?.[0];
    const fallbackPage = createQuestionPage({ title: 'Fragen', order: 1, questions: [] });
    const pageId = firstPage?.id || fallbackPage.id;

    const singlePage = {
        ...(firstPage || fallbackPage),
        id: pageId,
        title: firstPage?.title || fallbackPage.title,
        description: firstPage?.description || '',
        order: 1,
        questions: flattenedQuestions.map((question) => ({
            ...question,
            pageId
        }))
    };

    return normalizeQuestionnaireData({
        ...normalized,
        questionPages: [singlePage],
        questions: singlePage.questions
    });
};

// --- Notification Component ---
function Notification({ message, type = 'success', onClose }) {
  useEffect(() => {
    const timer = setTimeout(() => {
      onClose();
    }, 3000); // Auto-hide after 3 seconds

    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div className="fixed top-4 right-4 z-50 transform transition-all duration-300 ease-out animate-pulse">
      <div className="bg-white dark:bg-slate-800 border border-green-200 dark:border-green-700 rounded-lg shadow-lg p-4 flex items-center gap-3 min-w-[200px]">
        <div className="shrink-0">
          <svg className="w-6 h-6 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <div className="flex-1">
          <p className="text-sm font-medium text-slate-900 dark:text-slate-100">{message}</p>
        </div>
        <button
          onClick={onClose}
          className="shrink-0 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>
  );
}

// --- Question Component ---
function QuestionCard({
  question,
  index,
  updateQuestion,
  removeQuestion,
  onDragStart,
  onDragEnd,
  setDraggedIndex,
  setDragOverIndex,
  pageOptions = [],
  onMoveToPage
}) {

  const addOption = (questionId) => {
    const currentOptions = question.options || [];
    updateQuestion(questionId, { options: [...currentOptions, ''] });
  };

  const removeOption = (questionId, optionIndex) => {
    const currentOptions = question.options || [];
    const newOptions = currentOptions.filter((_, index) => index !== optionIndex);
    updateQuestion(questionId, { options: newOptions });
  };

  const handleOptionChange = (questionId, optionIndex, value) => {
    const currentOptions = question.options || [];
    const newOptions = [...currentOptions];
    newOptions[optionIndex] = value;
    updateQuestion(questionId, { options: newOptions });
  };

  const handleCorrectAnswerChange = (questionId, optionIndex, isCorrect) => {
    const currentCorrectAnswers = question.correctAnswers || [];
    let newCorrectAnswers;
    
    if (question.type === 'single-choice') {
      // For single choice exclusion criteria, allow multiple correct answers
      if (isCorrect) {
        newCorrectAnswers = [...currentCorrectAnswers, optionIndex];
      } else {
        newCorrectAnswers = currentCorrectAnswers.filter(index => index !== optionIndex);
      }
    } else {
      // For multiple choice, multiple answers can be correct
      if (isCorrect) {
        newCorrectAnswers = [...currentCorrectAnswers, optionIndex];
      } else {
        newCorrectAnswers = currentCorrectAnswers.filter(index => index !== optionIndex);
      }
    }
    
    updateQuestion(questionId, { correctAnswers: newCorrectAnswers });
  };

  return (
    <div className={`bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 p-4 sm:p-5 rounded-xl`}>
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center gap-3">
          <div 
            draggable
            onDragStart={(e) => onDragStart(e, index)}
            onDragEnd={onDragEnd}
            className="drag-handle flex flex-col items-center text-slate-400 dark:text-slate-500 cursor-grab active:cursor-grabbing hover:text-slate-600 dark:hover:text-slate-300 transition-colors duration-200 p-1 rounded"
          >
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
              <path d="M9 3a1 1 0 1 1-2 0 1 1 0 0 1 2 0zM9 7a1 1 0 1 1-2 0 1 1 0 0 1 2 0zM9 11a1 1 0 1 1-2 0 1 1 0 0 1 2 0zM9 15a1 1 0 1 1-2 0 1 1 0 0 1 2 0zM9 19a1 1 0 1 1-2 0 1 1 0 0 1 2 0zM15 3a1 1 0 1 1-2 0 1 1 0 0 1 2 0zM15 7a1 1 0 1 1-2 0 1 1 0 0 1 2 0zM15 11a1 1 0 1 1-2 0 1 1 0 0 1 2 0zM15 15a1 1 0 1 1-2 0 1 1 0 0 1 2 0zM15 19a1 1 0 1 1-2 0 1 1 0 0 1 2 0z"/>
            </svg>
          </div>
          <span className="font-bold text-base sm:text-lg text-slate-700 dark:text-slate-200">Frage {index + 1}</span>
        </div>
        <div className="flex items-center gap-3 sm:gap-4">
          <select
            value={question.type}
            onChange={(e) => {
              const newType = e.target.value;
              const updates = { type: newType };
              if (newType === 'ranking') {
                updates.options = (question.options && question.options.length >= 2) ? question.options : ['Option 1', 'Option 2', 'Option 3'];
              }
              if (newType === 'image') {
                updates.answerType = question.answerType || 'text';
                updates.options = question.answerType === 'single-choice' || question.answerType === 'multiple-choice' ? (question.options || ['']) : [];
              }
              updateQuestion(question.id, updates);
            }}
            onMouseDown={(e) => {
              e.stopPropagation();
            }}
            onDragStart={(e) => e.stopPropagation()}
            className="dropdown-select p-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-200 text-sm sm:text-base"
          >
            <option value="text">Text</option>
            <option value="single-choice">Single-Choice</option>
            <option value="multiple-choice">Multiple-Choice</option>
            <option value="free-text">Freitext</option>
            <option value="ranking">Ranking</option>
            <option value="image">Bild</option>
          </select>
          <button 
            onClick={() => removeQuestion(question.id)} 
            onMouseDown={(e) => e.stopPropagation()}
            onDragStart={(e) => e.stopPropagation()}
            className="p-2 text-slate-400 dark:text-slate-300 hover:bg-red-100 dark:hover:bg-red-900/20 hover:text-red-600 dark:hover:text-red-400 rounded-full"
          >
            <TrashIcon/>
          </button>
        </div>
      </div>

      {pageOptions.length > 1 && (
        <div className="mb-4">
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
            Seite
          </label>
          <select
            value={question.pageId || ''}
            onChange={(e) => onMoveToPage(question.id, e.target.value)}
            onMouseDown={(e) => e.stopPropagation()}
            onDragStart={(e) => e.stopPropagation()}
            className="dropdown-select p-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-200 text-sm sm:text-base"
          >
            {pageOptions.map((page) => (
              <option key={page.id} value={page.id}>
                {page.title}
              </option>
            ))}
          </select>
        </div>
      )}

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Fragetext</label>
          <textarea
            value={question.text}
            onChange={(e) => updateQuestion(question.id, { text: e.target.value })}
            onMouseDown={(e) => e.stopPropagation()}
            onDragStart={(e) => e.stopPropagation()}
            onClick={() => { setDraggedIndex(null); setDragOverIndex(null); }}
            className="w-full p-3 border border-slate-300 dark:border-slate-600 rounded-lg text-base bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-200 resize-none"
            rows={3}
            placeholder="Geben Sie hier Ihre Frage ein..."
          />
        </div>

        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id={`required-${question.id}`}
              checked={question.required !== false}
              onChange={(e) => updateQuestion(question.id, { required: e.target.checked })}
              onMouseDown={(e) => e.stopPropagation()}
              onDragStart={(e) => e.stopPropagation()}
              className="w-4 h-4 text-indigo-600 bg-gray-100 border-gray-300 rounded focus:ring-indigo-500"
            />
            <label htmlFor={`required-${question.id}`} className="text-sm font-medium text-slate-700 dark:text-slate-300">
              Erforderlich
            </label>
          </div>
          
          {(question.type === 'single-choice' || question.type === 'multiple-choice') && (
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id={`critical-${question.id}`}
                checked={question.critical === true}
                onChange={(e) => updateQuestion(question.id, { critical: e.target.checked })}
                onMouseDown={(e) => e.stopPropagation()}
                onDragStart={(e) => e.stopPropagation()}
                className="w-4 h-4 text-red-600 bg-gray-100 border-gray-300 rounded focus:ring-red-500"
              />
              <label htmlFor={`critical-${question.id}`} className="text-sm font-medium text-slate-700 dark:text-slate-300">
                Korrekte Antwort(en) festlegen (Zulassungskriterium)
              </label>
            </div>
          )}
        </div>

        {(question.type === 'single-choice' || question.type === 'multiple-choice' || question.type === 'ranking') && (
          <div className="question-options pl-4 border-l-2 border-green-200 dark:border-green-700">
            <h4 className="font-semibold text-slate-600 dark:text-slate-300 mb-2">
              {question.type === 'ranking' ? 'Optionen (Reihenfolge wählen)' : 'Antwortoptionen'}
            </h4>
            <div className="space-y-2">
              {(question.options || []).map((opt, optIndex) => (
                <div key={optIndex} className="flex items-center gap-2">
                  <input 
                    type="text" 
                    value={opt} 
                    onChange={(e) => handleOptionChange(question.id, optIndex, e.target.value)} 
                    onMouseDown={(e) => e.stopPropagation()}
                    onDragStart={(e) => e.stopPropagation()}
                    onClick={() => { setDraggedIndex(null); setDragOverIndex(null); }}
                    className="option-input grow p-2 border border-slate-300 dark:border-slate-600 rounded-md text-base bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-200"
                    placeholder={`Option ${optIndex + 1}`}
                  />
                  
                  {/* Correct Answer Checkbox - only for single/multiple choice when critical */}
                  {question.type !== 'ranking' && question.critical && (
                    <div className="flex items-center gap-1">
                      <input
                        type="checkbox"
                        name={`correct-${question.id}-${optIndex}`}
                        checked={(question.correctAnswers || []).includes(optIndex)}
                        onChange={(e) => handleCorrectAnswerChange(question.id, optIndex, e.target.checked)}
                        onMouseDown={(e) => e.stopPropagation()}
                        onDragStart={(e) => e.stopPropagation()}
                        className="w-4 h-4 text-green-600 bg-gray-100 border-gray-300 rounded focus:ring-green-500"
                        title="Korrekte Antwort"
                      />
                      <span className="text-xs text-green-600 dark:text-green-400 font-medium">
                        {question.type === 'single-choice' ? '✓' : '✓'}
                      </span>
                    </div>
                  )}
                  
                  <button 
                    onClick={() => removeOption(question.id, optIndex)} 
                    onMouseDown={(e) => e.stopPropagation()}
                    onDragStart={(e) => e.stopPropagation()}
                    className="p-2 text-slate-400 dark:text-slate-300 hover:text-red-600 dark:hover:text-red-400 rounded-full"
                  >
                    <MinusCircleIcon />
                  </button>
                </div>
              ))}
              <button 
                onClick={() => addOption(question.id)} 
                onMouseDown={(e) => e.stopPropagation()}
                onDragStart={(e) => e.stopPropagation()}
                className="mt-2 px-3 py-2 text-sm sm:text-base bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200 font-semibold rounded-md hover:bg-green-200 dark:hover:bg-green-900/50"
              >
                + Option
              </button>
            </div>
            
            {question.type === 'ranking' && (
              <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
                Kandidaten ordnen die Optionen in die richtige Reihenfolge.
              </p>
            )}
            
            {/* Show hint for critical questions */}
            {question.type !== 'ranking' && question.critical && (
              <div className="mt-3 p-3 bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-600 rounded-lg">
                <p className="text-sm text-amber-900 dark:text-amber-100">
                  <span className="font-semibold">Korrekte Antwort(en) festlegen (Zulassungskriterium):</span> Markiere alle korrekten Antworten. 
                  Bei falscher Antwort wird das Quiz beendet und der konfigurierte Text angezeigt.
                </p>
              </div>
            )}
          </div>
        )}

        {(question.type === 'free-text' || question.type === 'text') && (
          <div className="p-4 bg-slate-50 dark:bg-slate-700 rounded-lg">
            <p className="text-sm text-slate-600 dark:text-slate-400">
              Kandidaten können hier eine freie Textantwort eingeben.
            </p>
          </div>
        )}

        {question.type === 'image' && (
          <div className="space-y-4 pl-4 border-l-2 border-green-200 dark:border-green-700">
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Bild</label>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  const reader = new FileReader();
                  reader.onload = () => updateQuestion(question.id, { imageDataUrl: reader.result, file });
                  reader.readAsDataURL(file);
                }}
                onMouseDown={(e) => e.stopPropagation()}
                onDragStart={(e) => e.stopPropagation()}
                className="block w-full text-sm text-slate-600 dark:text-slate-400 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-indigo-50 file:text-indigo-700 dark:file:bg-indigo-900/30 dark:file:text-indigo-300"
              />
              {(question.imageDataUrl || question.imageUrl) && (
                <div className="mt-2 relative inline-block">
                  <img
                    src={question.imageDataUrl || question.imageUrl}
                    alt="Vorschau"
                    className="max-h-40 rounded-lg border border-slate-200 dark:border-slate-600"
                  />
                  <button
                    type="button"
                    onClick={() => updateQuestion(question.id, { imageDataUrl: null, imageUrl: null, file: null })}
                    className="absolute top-1 right-1 p-1 bg-red-100 dark:bg-red-900/50 text-red-600 rounded-full hover:bg-red-200 dark:hover:bg-red-800"
                    aria-label="Bild entfernen"
                  >
                    <TrashIcon className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Antwortformat</label>
              <select
                value={question.answerType || 'text'}
                onChange={(e) => {
                  const answerType = e.target.value;
                  const updates = { answerType };
                  if (answerType === 'single-choice' || answerType === 'multiple-choice') {
                    updates.options = question.options?.length ? question.options : ['', ''];
                  } else {
                    updates.options = [];
                  }
                  updateQuestion(question.id, updates);
                }}
                onMouseDown={(e) => e.stopPropagation()}
                onDragStart={(e) => e.stopPropagation()}
                className="p-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-200 text-sm"
              >
                <option value="text">Text</option>
                <option value="single-choice">Single-Choice</option>
                <option value="multiple-choice">Multiple-Choice</option>
              </select>
            </div>
            {(question.answerType === 'single-choice' || question.answerType === 'multiple-choice') && (
              <div className="space-y-2">
                <h4 className="font-semibold text-slate-600 dark:text-slate-300 text-sm">Antwortoptionen</h4>
                {(question.options || []).map((opt, optIndex) => (
                  <div key={optIndex} className="flex items-center gap-2">
                    <input
                      type="text"
                      value={opt}
                      onChange={(e) => handleOptionChange(question.id, optIndex, e.target.value)}
                      onMouseDown={(e) => e.stopPropagation()}
                      onDragStart={(e) => e.stopPropagation()}
                      className="grow p-2 border border-slate-300 dark:border-slate-600 rounded-md text-sm bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-200"
                      placeholder={`Option ${optIndex + 1}`}
                    />
                    <button
                      type="button"
                      onClick={() => removeOption(question.id, optIndex)}
                      onMouseDown={(e) => e.stopPropagation()}
                      className="p-2 text-slate-400 hover:text-red-600 rounded-full"
                    >
                      <MinusCircleIcon className="w-4 h-4" />
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => addOption(question.id)}
                  onMouseDown={(e) => e.stopPropagation()}
                  className="mt-1 px-3 py-2 text-sm bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200 font-semibold rounded-md hover:bg-green-200 dark:hover:bg-green-900/50"
                >
                  + Option
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}


// --- Template Confirmation Modal Component ---
function TemplateConfirmModal({ templateName, onConfirm, onCancel }) {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-slate-800 dark:text-slate-200 p-6 rounded-2xl shadow-2xl max-w-md w-full m-4 transform transition-all duration-300">
        <div className="flex items-center gap-3 mb-4">
          <div className="shrink-0">
            <svg className="w-8 h-8 text-amber-600 dark:text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-slate-800 dark:text-slate-100">Vorlage überschreiben?</h2>
        </div>
        <p className="text-slate-600 dark:text-slate-300 mb-6">
          Eine Vorlage mit dem Namen "{templateName}" existiert bereits. Möchten Sie sie überschreiben?
        </p>
        <div className="flex gap-3 justify-end">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
          >
            Abbrechen
          </button>
          <button
            onClick={onConfirm}
            className="px-4 py-2 bg-amber-600 text-white hover:bg-amber-700 rounded-lg transition-colors"
          >
            Überschreiben
          </button>
        </div>
      </div>
    </div>
  );
}

// --- Delete Confirmation Modal Component ---
function DeleteConfirmModal({ templateName, onConfirm, onCancel }) {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-slate-800 dark:text-slate-200 p-6 rounded-2xl shadow-2xl max-w-md w-full m-4 transform transition-all duration-300">
        <div className="flex items-center gap-3 mb-4">
          <div className="shrink-0">
            <svg className="w-8 h-8 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-slate-800 dark:text-slate-100">Vorlage löschen?</h2>
        </div>
        <p className="text-slate-600 dark:text-slate-300 mb-6">
          Möchten Sie die Vorlage <span className="font-semibold text-slate-800 dark:text-slate-200">"{templateName}"</span> wirklich löschen? 
          Diese Aktion kann nicht rückgängig gemacht werden.
        </p>
        <div className="flex gap-3 justify-end">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
          >
            Abbrechen
          </button>
          <button
            onClick={onConfirm}
            className="px-4 py-2 bg-red-600 text-white hover:bg-red-700 rounded-lg transition-colors"
          >
            Löschen
          </button>
        </div>
      </div>
    </div>
  );
}

export function FragebogenCreationPage({ campaign, onBack, onSave }) {
    const [fragebogenData, setFragebogenData] = useState(() => ensureSingleQuizPageData({
        title: '',
        description: '',
        conclusionText: '',
        questions: []
    }));
    const [selectedPageId, setSelectedPageId] = useState(null);
    const [notification, setNotification] = useState(null);
    const [draggedIndex, setDraggedIndex] = useState(null);
    const [dragOverIndex, setDragOverIndex] = useState(null);
    
    // Template management state
    const [templates, setTemplates] = useState([]);
    const [templateName, setTemplateName] = useState('');
    const [selectedTemplate, setSelectedTemplate] = useState('');
    const [showTemplateConfirmModal, setShowTemplateConfirmModal] = useState(false);
    const [pendingTemplateData, setPendingTemplateData] = useState(null);
    
    // Delete confirmation modal state
    const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
    const [pendingDeleteData, setPendingDeleteData] = useState(null);

    // Link modal state
    const [generatedLink, setGeneratedLink] = useState(null);
    const [showLinkModal, setShowLinkModal] = useState(false);

    // Type for next question to add (for "Frage hinzufügen")
    const [nextQuestionType, setNextQuestionType] = useState('single-choice');

    // Load existing quiz data if available
    useEffect(() => {
        if (campaign?.questionnaire_data) {
            const normalizedData = ensureSingleQuizPageData(campaign.questionnaire_data);
            setFragebogenData(normalizedData);
            setSelectedPageId(normalizedData.questionPages?.[0]?.id || null);
        }
    }, [campaign]);

    useEffect(() => {
        const hasSelectedPage = fragebogenData.questionPages?.some((page) => page.id === selectedPageId);
        if (!hasSelectedPage) {
            setSelectedPageId(fragebogenData.questionPages?.[0]?.id || null);
        }
    }, [fragebogenData.questionPages, selectedPageId]);

    // Load templates on component mount with cleanup
    useEffect(() => {
        let isMounted = true;
        
        const loadTemplatesWithCleanup = async () => {
            try {
                const templatesData = await quizTemplateService.getTemplates();
                if (isMounted) {
                    setTemplates(templatesData);
                }
            } catch (error) {
                // Only show error if component is still mounted and it's not a cancellation
                if (isMounted && error.name !== 'AbortError') {
                    console.error('Error loading templates:', error);
                    showNotification('Fehler beim Laden der Vorlagen', 'error');
                }
            }
        };
        
        loadTemplatesWithCleanup();
        
        return () => {
            isMounted = false;
            // Cleanup any pending requests
            quizTemplateService.cleanup();
        };
    }, []);

    const showNotification = (message, type = 'success') => {
        setNotification({ message, type });
    };

    const hideNotification = () => {
        setNotification(null);
    };

    const activePage = fragebogenData.questionPages?.find((page) => page.id === selectedPageId) || fragebogenData.questionPages?.[0];
    const activeQuestions = activePage?.questions || [];

    const updateQuestionPages = useCallback((updater) => {
        setFragebogenData((prev) => {
            const currentPages = prev.questionPages || [];
            const nextPages = typeof updater === 'function' ? updater(currentPages) : updater;
            return normalizeQuestionnaireData({
                ...prev,
                questionPages: nextPages
            });
        });
    }, []);

    const addQuestion = (type = 'single-choice') => {
        if (!activePage) {
            showNotification('Bitte zuerst eine Seite auswählen.', 'error');
            return;
        }

        const needsOptions = type === 'single-choice' || type === 'multiple-choice' || type === 'ranking';
        const defaultOptions = type === 'ranking' ? ['Option 1', 'Option 2', 'Option 3'] : ['', ''];
        const newQuestion = {
            id: Date.now(),
            text: '',
            type,
            options: needsOptions ? defaultOptions : [],
            required: true,
            critical: false,
            correctAnswers: [],
            pageId: activePage.id,
            ...(type === 'image' && { answerType: 'text' })
        };
        updateQuestionPages((pages) => pages.map((page) => (
            page.id === activePage.id
                ? { ...page, questions: [...(page.questions || []), newQuestion] }
                : page
        )));
    };

    const updateQuestion = (questionId, updates) => {
        updateQuestionPages((pages) => pages.map((page) => ({
            ...page,
            questions: (page.questions || []).map((q) => {
                if (q.id !== questionId) return q;
                const merged = { ...q, ...updates };
                if (updates.type === 'ranking' && (!merged.options || merged.options.length < 2)) {
                    merged.options = ['Option 1', 'Option 2', 'Option 3'];
                }
                if (updates.type === 'image' && merged.answerType === undefined) {
                    merged.answerType = 'text';
                }
                return merged;
            })
        })));
    };

    const removeQuestion = (questionId) => {
        updateQuestionPages((pages) => pages.map((page) => ({
            ...page,
            questions: (page.questions || []).filter((q) => q.id !== questionId)
        })));
    };

    // const duplicateQuestion = (questionId) => {
    //     const question = fragebogenData.questions.find(q => q.id === questionId);
    //     if (question) {
    //         const newQuestion = {
    //             ...question,
    //             id: Date.now(),
    //             text: question.text + ' (Kopie)'
    //         };
    //         setFragebogenData(prev => ({
    //             ...prev,
    //             questions: [...prev.questions, newQuestion]
    //         }));
    //     }
    // };

    const handleDragStart = (e, index) => {
        // Prevent dragging when interacting with form elements or interactive content
        const target = e.target;
        
        // Check if the target or any of its ancestors is an interactive element
        const isInteractiveElement = (element) => {
            if (!element) return false;
            
            // Check the element itself
            if (element.tagName === 'INPUT' || 
                element.tagName === 'TEXTAREA' || 
                element.tagName === 'SELECT' || 
                element.tagName === 'BUTTON' ||
                element.contentEditable === 'true' ||
                element.closest('input') ||
                element.closest('textarea') ||
                element.closest('select') ||
                element.closest('button') ||
                element.closest('[contenteditable="true"]') ||
                element.closest('[role="button"]') ||
                element.closest('[data-interactive]') ||
                element.closest('.formatting-toolbar') ||
                element.closest('.question-options') ||
                element.closest('.file-input-container') ||
                element.closest('.content-editable-field')) {
                return true;
            }
            
            // Check if it's a formatting button or similar interactive element
            if (element.classList.contains('format-button') ||
                element.classList.contains('option-input') ||
                element.classList.contains('file-input') ||
                element.classList.contains('dropdown-select') ||
                element.classList.contains('content-editable-field')) {
                return true;
            }
            
            // Check if it's the drag handle area (the dots icon)
            if (element.closest('.drag-handle') || element.closest('.cursor-grab') || element.closest('.cursor-grabbing')) {
                return false; // Allow dragging from the drag handle
            }
            
            return false;
        };
        
        // Check the target and all its ancestors
        let currentElement = target;
        while (currentElement && currentElement !== document.body) {
            if (isInteractiveElement(currentElement)) {
                e.preventDefault();
                return false;
            }
            currentElement = currentElement.parentElement;
        }
        
        setDraggedIndex(index);
        e.dataTransfer.effectAllowed = 'move';
    };

    const handleDragOver = (e, index) => {
        e.preventDefault();
        if (draggedIndex !== null) {
            setDragOverIndex(index);
        }
    };

    const handleDragLeave = () => {
        setDragOverIndex(null);
    };

    const handleDrop = (e, dropIndex) => {
        e.preventDefault();
        if (draggedIndex === null || draggedIndex === dropIndex) return;

        if (!activePage) return;

        const newQuestions = [...activeQuestions];
        const draggedQuestion = newQuestions[draggedIndex];
        newQuestions.splice(draggedIndex, 1);
        newQuestions.splice(dropIndex, 0, draggedQuestion);

        updateQuestionPages((pages) => pages.map((page) => (
            page.id === activePage.id ? { ...page, questions: newQuestions } : page
        )));
        setDraggedIndex(null);
        setDragOverIndex(null);
    };

    const handleDragEnd = () => {
        setDraggedIndex(null);
        setDragOverIndex(null);
    };

    const validateQuiz = () => {
        if (!fragebogenData.title.trim()) {
            showNotification('Bitte geben Sie einen Titel für das Quiz ein.', 'error');
            return false;
        }
        if (fragebogenData.questions.length === 0) {
            showNotification('Bitte fügen Sie mindestens eine Frage hinzu.', 'error');
            return false;
        }
        for (let i = 0; i < fragebogenData.questions.length; i++) {
            const question = fragebogenData.questions[i];
            if (!question.text.trim() && question.type !== 'image') {
                showNotification(`Bitte geben Sie einen Text für Frage ${i + 1} ein.`, 'error');
                return false;
            }
            const needsOptions = ['single-choice', 'multiple-choice', 'ranking'].includes(question.type) ||
                (question.type === 'image' && (question.answerType === 'single-choice' || question.answerType === 'multiple-choice'));
            if (needsOptions) {
                const opts = question.options || [];
                if (opts.length < 2) {
                    showNotification(`Frage ${i + 1} benötigt mindestens 2 Antwortoptionen.`, 'error');
                    return false;
                }
                const hasEmptyOptions = opts.some(opt => !String(opt).trim());
                if (hasEmptyOptions) {
                    showNotification(`Frage ${i + 1} hat leere Antwortoptionen.`, 'error');
                    return false;
                }
            }
            if (question.type === 'image' && !question.imageDataUrl && !question.imageUrl) {
                showNotification(`Frage ${i + 1} (Bild): Bitte ein Bild auswählen.`, 'error');
                return false;
            }
            if (question.critical && (question.type === 'single-choice' || question.type === 'multiple-choice')) {
                if (!question.correctAnswers || question.correctAnswers.length === 0) {
                    showNotification(`Korrekte Antwort(en) festlegen (Zulassungskriterium) ${i + 1} benötigt mindestens eine korrekte Antwort.`, 'error');
                    return false;
                }
                const validAnswers = question.correctAnswers.every(answerIndex => 
                    answerIndex >= 0 && answerIndex < (question.options || []).length
                );
                if (!validAnswers) {
                    showNotification(`Frage ${i + 1} hat ungültige korrekte Antworten.`, 'error');
                    return false;
                }
            }
        }
        return true;
    };

    // Upload image questions to PocketBase and return data without base64/file (same as QuestionnaireEditor)
    const processImageQuestionsForSave = useCallback(async (data) => {
        const singlePageData = ensureSingleQuizPageData(data);
        const questions = (singlePageData.questions || []).map(async (q) => {
            if (q.type !== 'image') return q;
            if (q.imageUrl && q.imageId) {
                return { ...q, file: null, imageDataUrl: null };
            }
            if (q.file) {
                try {
                    const uploaded = await imageUploadService.uploadQuestionnaireImage(q.file, {
                        title: q.text || 'Quiz Bild',
                        questionnaire_id: campaign?.id || ''
                    });
                    return {
                        ...q,
                        imageUrl: uploaded.url,
                        imageId: uploaded.id,
                        imageDataUrl: null,
                        file: null
                    };
                } catch (err) {
                    console.error('Bild-Upload fehlgeschlagen:', err);
                    throw new Error(`Bild konnte nicht hochgeladen werden: ${err.message}`);
                }
            }
            return q;
        });
        const resolved = await Promise.all(questions);
        const byId = new Map(resolved.map((question) => [String(question.id), question]));
        const questionPages = (singlePageData.questionPages || []).map((page) => ({
            ...page,
            questions: (page.questions || []).map((question) => (
                byId.get(String(question.id)) || question
            ))
        }));
        return normalizeQuestionnaireData({
            ...singlePageData,
            questionPages,
            questions: resolved
        });
    }, [campaign?.id]);

    const handleSave = async () => {
        if (!validateQuiz()) return;
        try {
            const dataToSave = await processImageQuestionsForSave(fragebogenData);
            onSave(dataToSave);

            const applicantBase = 'https://karrieresichern.de';
            const link = `${applicantBase}/apply/${campaign.id}`;
            setGeneratedLink(link);
            setShowLinkModal(true);
            showNotification('Quiz erfolgreich gespeichert!');
        } catch (err) {
            showNotification(err.message || 'Speichern fehlgeschlagen', 'error');
        }
    };

    // Template management functions - exactly like questionnaire editor
    const loadTemplates = useCallback(async () => {
        try {
            const templatesData = await quizTemplateService.getTemplates();
            setTemplates(templatesData);
        } catch (error) {
            // Only show error if it's not a cancellation
            if (error.name !== 'AbortError') {
                console.error('Error loading templates:', error);
                showNotification('Fehler beim Laden der Vorlagen', 'error');
            }
        }
    }, []);

    // Debounced version to prevent rapid successive calls
    // const debouncedLoadTemplates = useCallback(() => {
    //     const timeoutId = setTimeout(() => {
    //         loadTemplates();
    //     }, 300); // 300ms debounce
    //     
    //     return () => clearTimeout(timeoutId);
    // }, [loadTemplates]);

    const saveAsTemplate = async () => {
        if (!templateName.trim() || fragebogenData.questions.length === 0) {
            showNotification('Bitte geben Sie einen Vorlagennamen ein und fügen Sie Fragen hinzu.', 'error');
            return;
        }
        
        try {
            // Check if template with same name already exists
            const allTemplates = await quizTemplateService.getTemplates();
            const existingTemplate = allTemplates.find(t => t.name.trim().toLowerCase() === templateName.trim().toLowerCase());
            
            if (existingTemplate) {
                // Show confirmation modal instead of browser alert
                setPendingTemplateData({ existingTemplate, templateName });
                setShowTemplateConfirmModal(true);
                return;
            }
            
            // If no existing template, save directly
            await saveTemplateData(null);
        } catch (error) {
            console.error('Error saving template:', error);
            showNotification(`Fehler beim Speichern der Vorlage: ${error.message}`, 'error');
        }
    };

    const saveTemplateData = async (existingTemplate = null) => {
        try {
            const processedQuiz = await processImageQuestionsForSave(fragebogenData);
            const templateData = {
                name: templateName.trim(),
                quiz: processedQuiz
            };

            if (existingTemplate) {
                // Update existing template
                await quizTemplateService.updateTemplate(existingTemplate.id, templateData);
                console.log('Template updated in PocketBase successfully');
            } else {
                // Create new template
                await quizTemplateService.createTemplate(templateData);
                console.log('Template saved to PocketBase successfully');
            }
            
            // Refresh templates from PocketBase
            await loadTemplates();
            
            setTemplateName('');
            const action = existingTemplate ? "überschrieben" : "gespeichert";
            showNotification(`Vorlage "${templateName}" wurde ${action}!`);
        } catch (error) {
            console.error('Error saving template:', error);
            showNotification(`Fehler beim Speichern der Vorlage: ${error.message}`, 'error');
        }
    };

    const loadFromTemplate = async (templateId) => {
        try {
            const template = await quizTemplateService.getTemplate(templateId);
            const normalizedQuiz = ensureSingleQuizPageData(template.quiz);
            setFragebogenData(normalizedQuiz);
            setSelectedPageId(normalizedQuiz.questionPages?.[0]?.id || null);
            showNotification(`Vorlage "${template.name}" wurde geladen!`);
        } catch (error) {
            console.error('Error loading template:', error);
            showNotification('Fehler beim Laden der Vorlage', 'error');
        }
    };

    const deleteTemplate = async (templateId, templateName) => {
        if (!templateId || !templateName) return;
        
        // Show confirmation modal instead of browser confirm
        setPendingDeleteData({ templateId, templateName });
        setShowDeleteConfirmModal(true);
    };

    const confirmDeleteTemplate = async () => {
        if (!pendingDeleteData) return;
        
        const { templateId, templateName } = pendingDeleteData;
        
        try {
            await quizTemplateService.deleteTemplate(templateId);
            await loadTemplates();
            setSelectedTemplate('');
            showNotification(`Vorlage "${templateName}" wurde gelöscht!`);
        } catch (error) {
            console.error('Error deleting template:', error);
            showNotification('Fehler beim Löschen der Vorlage', 'error');
        } finally {
            setShowDeleteConfirmModal(false);
            setPendingDeleteData(null);
        }
    };

    return (
        <div className="min-h-screen py-8 relative overflow-hidden">
            {/* Notification */}
            {notification && (
                <Notification 
                    message={notification.message} 
                    type={notification.type} 
                    onClose={hideNotification} 
                />
            )}

            {/* Dynamic Flowing Lines */}
            <FlowingLines theme="emerald" opacity="60" />

            <div className="max-w-3xl sm:max-w-4xl lg:max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                <header className="mb-8 text-center">
                    <ModernButton 
                        onClick={onBack} 
                        variant="ghost" 
                        size="sm" 
                        className="mb-6"
                        icon={<ArrowLeftIcon className="w-4 h-4" />}
                    >
                        Zurück zum Bewerbungspool
                    </ModernButton>
                    <h1 className="text-3xl md:text-5xl font-bold rainbow-headline mb-6 leading-tight pb-2">
                        Quiz erstellen
                    </h1>
                    <p className="text-xl text-slate-600 dark:text-slate-300 max-w-2xl mx-auto mt-2">
                        Erstelle ein interaktives Quiz für deine Bewerber
                    </p>
                    
                </header>

                {/* Quiz Basic Info */}
                <div className="bg-white/90 dark:bg-slate-800/90 backdrop-blur-md border border-slate-200/60 dark:border-slate-700/60 p-6 rounded-2xl shadow-2xl dark:shadow-2xl mb-8 relative overflow-hidden transform transition-all duration-300 hover:scale-[1.01] hover:shadow-3xl">
                    <h2 className="text-lg sm:text-xl font-bold text-slate-800 dark:text-slate-100 mb-4">
                        Quiz-Grunddaten
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                                Quiz-Titel *
                            </label>
                            <input
                                type="text"
                                value={fragebogenData.title}
                                onChange={(e) => setFragebogenData(prev => ({ ...prev, title: e.target.value }))}
                                onMouseDown={(e) => e.stopPropagation()}
                                onDragStart={(e) => e.stopPropagation()}
                                onClick={() => { setDraggedIndex(null); setDragOverIndex(null); }}
                                className="w-full p-3 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                placeholder="z.B. Quiz für Softwareentwickler"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                                Einleitungstext
                            </label>
                            <input
                                type="text"
                                value={fragebogenData.description}
                                onChange={(e) => setFragebogenData(prev => ({ ...prev, description: e.target.value }))}
                                onMouseDown={(e) => e.stopPropagation()}
                                onDragStart={(e) => e.stopPropagation()}
                                onClick={() => { setDraggedIndex(null); setDragOverIndex(null); }}
                                className="w-full p-3 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                placeholder="Text der am Anfang des Quiz angezeigt wird"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                                Vorschau-Titel
                            </label>
                            <input
                                type="text"
                                value={fragebogenData.previewTitle || "Was Sie erwartet"}
                                onChange={(e) => setFragebogenData(prev => ({ ...prev, previewTitle: e.target.value }))}
                                onMouseDown={(e) => e.stopPropagation()}
                                onDragStart={(e) => e.stopPropagation()}
                                onClick={() => { setDraggedIndex(null); setDragOverIndex(null); }}
                                className="w-full p-3 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                placeholder="Titel der Vorschau-Seite"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                                Vorschau-Beschreibung
                            </label>
                            <textarea
                                value={fragebogenData.previewDescription || "Beantworten Sie verschiedene Fragetypen."}
                                onChange={(e) => setFragebogenData(prev => ({ ...prev, previewDescription: e.target.value }))}
                                onMouseDown={(e) => e.stopPropagation()}
                                onDragStart={(e) => e.stopPropagation()}
                                onClick={() => { setDraggedIndex(null); setDragOverIndex(null); }}
                                className="w-full p-3 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
                                placeholder="Beschreibung der Vorschau-Seite"
                                rows={3}
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                                Abschlusstext
                            </label>
                            <textarea
                                value={fragebogenData.conclusionText}
                                onChange={(e) => setFragebogenData(prev => ({ ...prev, conclusionText: e.target.value }))}
                                onMouseDown={(e) => e.stopPropagation()}
                                onDragStart={(e) => e.stopPropagation()}
                                onClick={() => { setDraggedIndex(null); setDragOverIndex(null); }}
                                className="w-full p-3 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
                                placeholder="Text der nach dem Absenden des Quiz angezeigt wird"
                                rows={3}
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                                Korrekte Antwort(en) festlegen (Zulassungskriterium) Text
                            </label>
                            <textarea
                                value={fragebogenData.errorMessage || "Diesmal passt es leider nicht. Versuch es ein anderes Mal"}
                                onChange={(e) => setFragebogenData(prev => ({ ...prev, errorMessage: e.target.value }))}
                                onMouseDown={(e) => e.stopPropagation()}
                                onDragStart={(e) => e.stopPropagation()}
                                onClick={() => { setDraggedIndex(null); setDragOverIndex(null); }}
                                className="w-full p-3 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
                                placeholder="Text der bei falscher Antwort auf korrekte Antworten angezeigt wird"
                                rows={3}
                            />
                        </div>
                    </div>
                </div>

                {/* Template Management Section - exactly like questionnaire editor */}
                <div className="bg-white/90 dark:bg-slate-800/90 backdrop-blur-md border border-slate-200/60 dark:border-slate-700/60 p-6 rounded-2xl shadow-2xl dark:shadow-2xl mb-8 relative overflow-hidden">
                    <h2 className="text-lg sm:text-xl font-bold text-slate-800 dark:text-slate-100 mb-4">
                        Vorlagen
                    </h2>
                    <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
                        <div className="flex-1 max-w-2xl">
                            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2 text-center">Vorlage laden</label>
                            <div className="flex items-stretch gap-3">
                                <div className="relative flex items-center justify-center order-first">
                                    <button 
                                        onClick={() => {
                                            const clearedQuiz = ensureSingleQuizPageData({
                                                title: '',
                                                description: '',
                                                conclusionText: '',
                                                questions: []
                                            });
                                            setFragebogenData(clearedQuiz);
                                            setSelectedPageId(clearedQuiz.questionPages?.[0]?.id || null);
                                        }}
                                        className="h-full aspect-square bg-slate-600 text-white rounded-lg hover:bg-slate-700 flex items-center justify-center" 
                                        aria-label="Quiz leeren" 
                                        title="Quiz leeren"
                                    >
                                        <EraserIcon />
                                    </button>
                                    <span className="absolute -top-3 text-[10px] text-slate-500 dark:text-slate-400 leading-none">CLEAR</span>
                                </div>
                                <div className="self-stretch w-px bg-slate-300 dark:bg-slate-600 my-1" />
                                <select 
                                    value={selectedTemplate || ''} 
                                    onChange={(e) => { 
                                        const id = e.target.value; 
                                        setSelectedTemplate(id); 
                                        if (id) loadFromTemplate(id); 
                                    }} 
                                    className="w-full p-3 border border-slate-300 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 rounded-lg text-base"
                                >
                                    <option value="">Vorlage auswählen...</option>
                                    {templates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                                </select>
                                <button 
                                    onClick={() => deleteTemplate(selectedTemplate, templates.find(t => t.id === selectedTemplate)?.name)} 
                                    disabled={!selectedTemplate} 
                                    className="px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 flex items-center justify-center min-w-[44px]" 
                                    aria-label="Vorlage löschen" 
                                    title="Vorlage löschen"
                                >
                                    <TrashIcon />
                                </button>
                            </div>
                        </div>
                        <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 w-full sm:w-auto">
                            <div className="flex w-full sm:w-auto min-w-0 max-w-xs">
                                <input 
                                    type="text" 
                                    value={templateName} 
                                    onChange={(e) => setTemplateName(e.target.value)} 
                                    placeholder="Vorlagenname" 
                                    className="flex-1 sm:flex-none p-3 border border-slate-300 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 rounded-l-lg min-w-0 focus:outline-none focus:ring-0 focus:border-green-600" 
                                />
                                <button 
                                    onClick={saveAsTemplate} 
                                    className="px-3 py-3 bg-slate-600 text-white rounded-r-lg hover:bg-slate-700 flex items-center justify-center whitespace-nowrap" 
                                    aria-label="Vorlage speichern" 
                                    title="Vorlage speichern"
                                >
                                    <SaveIcon />
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Questions Section */}
                <div className="bg-white/90 dark:bg-slate-800/90 backdrop-blur-md border border-slate-200/60 dark:border-slate-700/60 p-6 rounded-2xl shadow-2xl dark:shadow-2xl mb-8 relative overflow-hidden">
                    <div className="flex items-center justify-between mb-6">
                        <h2 className="text-lg sm:text-xl font-bold text-slate-800 dark:text-slate-100">
                            Fragen ({fragebogenData.questions.length} gesamt)
                        </h2>
                        <div className="flex flex-wrap items-center gap-2">
                            <select
                                value={nextQuestionType}
                                onChange={(e) => setNextQuestionType(e.target.value)}
                                className="p-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-200 text-sm"
                            >
                                <option value="text">Text</option>
                                <option value="single-choice">Single-Choice</option>
                                <option value="multiple-choice">Multiple-Choice</option>
                                <option value="free-text">Freitext</option>
                                <option value="ranking">Ranking</option>
                                <option value="image">Bild</option>
                            </select>
                            <ModernButton
                                onClick={() => addQuestion(nextQuestionType)}
                                variant="primary"
                                size="sm"
                                icon={<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" /></svg>}
                            >
                                Frage hinzufügen
                            </ModernButton>
                        </div>
                    </div>

                    <div className="space-y-4">
                        {activeQuestions.map((question, index) => (
                            <div
                                key={question.id}
                                onDragOver={(e) => {
                                    e.preventDefault();
                                    if (draggedIndex !== null) {
                                        handleDragOver(e, index);
                                    }
                                }}
                                onDragLeave={(e) => {
                                    if (draggedIndex !== null) {
                                        handleDragLeave(e);
                                    }
                                }}
                                onDrop={(e) => {
                                    e.preventDefault();
                                    if (draggedIndex !== null) {
                                        handleDrop(e, index);
                                    }
                                }}
                                className={`transform transition-all duration-200 ${
                                    draggedIndex === index ? 'opacity-50 scale-95' : ''
                                } ${
                                    dragOverIndex === index && draggedIndex !== index ? 'border-2 border-dashed border-green-400 bg-green-50 dark:bg-green-900/20' : ''
                                }`}
                            >
                                <QuestionCard
                                    question={question}
                                    index={index}
                                    updateQuestion={updateQuestion}
                                    removeQuestion={removeQuestion}
                                    onDragStart={handleDragStart}
                                    onDragEnd={handleDragEnd}
                                    setDraggedIndex={setDraggedIndex}
                                    setDragOverIndex={setDragOverIndex}
                                    pageOptions={[]}
                                    onMoveToPage={() => {}}
                                />
                            </div>
                        ))}

                        {activeQuestions.length === 0 && (
                            <div className="text-center py-12">
                                <div className="w-16 h-16 bg-slate-100 dark:bg-slate-700 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <svg className="w-8 h-8 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                    </svg>
                                </div>
                                <h3 className="text-lg font-medium text-slate-600 dark:text-slate-400 mb-2">
                                    Noch keine Fragen vorhanden
                                </h3>
                                <p className="text-slate-500 dark:text-slate-500 mb-4">
                                    Fügen Sie jetzt die erste Frage hinzu.
                                </p>
                                <div className="flex flex-wrap items-center justify-center gap-2">
                                    <select
                                        value={nextQuestionType}
                                        onChange={(e) => setNextQuestionType(e.target.value)}
                                        className="p-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-200 text-sm"
                                    >
                                        <option value="text">Text</option>
                                        <option value="single-choice">Single-Choice</option>
                                        <option value="multiple-choice">Multiple-Choice</option>
                                        <option value="free-text">Freitext</option>
                                        <option value="ranking">Ranking</option>
                                        <option value="image">Bild</option>
                                    </select>
                                    <ModernButton
                                        onClick={() => addQuestion(nextQuestionType)}
                                        variant="primary"
                                        size="sm"
                                        icon={<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" /></svg>}
                                    >
                                        Erste Frage hinzufügen
                                    </ModernButton>
                                </div>
                            </div>
                        )}

                        {activeQuestions.length > 0 && (
                            <div className="flex flex-wrap items-center justify-center gap-2 pt-4">
                                <select
                                    value={nextQuestionType}
                                    onChange={(e) => setNextQuestionType(e.target.value)}
                                    className="p-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-200 text-sm"
                                >
                                    <option value="text">Text</option>
                                    <option value="single-choice">Single-Choice</option>
                                    <option value="multiple-choice">Multiple-Choice</option>
                                    <option value="free-text">Freitext</option>
                                    <option value="ranking">Ranking</option>
                                    <option value="image">Bild</option>
                                </select>
                                <ModernButton
                                    onClick={() => addQuestion(nextQuestionType)}
                                    variant="primary"
                                    size="sm"
                                    icon={<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" /></svg>}
                                >
                                    Weitere Frage hinzufügen
                                </ModernButton>
                            </div>
                        )}
                    </div>
                </div>

                {/* Save Button */}
                <div className="flex justify-center">
                    <ModernButton
                        onClick={handleSave}
                        variant="primary"
                        size="lg"
                        disabled={fragebogenData.questions.length === 0}
                        icon={<SaveIcon className="w-5 h-5" />}
                    >
                        Speichern
                    </ModernButton>
                </div>
            </div>

            {/* Template Confirmation Modal */}
            {showTemplateConfirmModal && pendingTemplateData && (
                <TemplateConfirmModal 
                    templateName={pendingTemplateData.templateName}
                    onConfirm={async () => {
                        setShowTemplateConfirmModal(false);
                        await saveTemplateData(pendingTemplateData.existingTemplate);
                        setPendingTemplateData(null);
                    }}
                    onCancel={() => {
                        setShowTemplateConfirmModal(false);
                        setPendingTemplateData(null);
                    }}
                />
            )}

            {/* Link Modal */}
            {showLinkModal && generatedLink && (
                <LinkModal link={generatedLink} onClose={() => setShowLinkModal(false)} />
            )}

            {/* Delete Confirmation Modal */}
            {showDeleteConfirmModal && pendingDeleteData && (
                <DeleteConfirmModal 
                    templateName={pendingDeleteData.templateName}
                    onConfirm={confirmDeleteTemplate}
                    onCancel={() => {
                        setShowDeleteConfirmModal(false);
                        setPendingDeleteData(null);
                    }}
                />
            )}
        </div>
    );
}

// --- LinkModal Component ---
function LinkModal({ link, onClose }) {
    const [copyText, setCopyText] = useState('Link kopieren');

    const handleCopy = () => {
        const textArea = document.createElement("textarea");
        textArea.value = link;
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        try {
            document.execCommand('copy');
            setCopyText('Kopiert!');
            setTimeout(() => setCopyText('Link kopieren'), 2000);
        } catch (err) {
            console.error('Fehler beim Kopieren: ', err);
        }
        document.body.removeChild(textArea);
    };

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white dark:bg-slate-800 dark:text-slate-200 p-8 rounded-2xl shadow-2xl max-w-md w-full m-4">
                <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-100 mb-4">Quiz erstellt!</h2>
                <p className="text-slate-600 dark:text-slate-300 mb-6">Teile den folgenden Link mit den Bewerbern.</p>
                <div className="flex gap-2">
                    <input type="text" value={link} readOnly className="w-full p-3 border border-slate-300 dark:border-slate-700 rounded-lg bg-slate-50 dark:bg-slate-900 dark:text-slate-200"/>
                    <ModernButton onClick={handleCopy} variant="success" size="md" className="w-40">
                        {copyText}
                    </ModernButton>
                </div>
                <ModernButton onClick={onClose} variant="secondary" size="lg" className="mt-6 w-full">
                    Schließen
                </ModernButton>
            </div>
        </div>
    );
}
